import { useQueryClient } from '@tanstack/react-query'
import { ExternalLink, RotateCcw, X } from 'lucide-react'
import { useState } from 'react'

import {
  destinationPostsKey,
  useDestinationPostAction,
  useDestinationPosts,
  type DestinationPost,
  type DestinationPostAction,
  type DestinationPostsPage,
} from '@/api/endpoints/destinations'
import { Button } from '@/components/ui/Button'
import { Chip } from '@/components/ui/Chip'
import { Pagination } from '@/components/ui/Pagination'
import { Segmented } from '@/components/ui/Segmented'
import { Spinner } from '@/components/ui/Spinner'
import { cn } from '@/lib/cn'
import { errorMessage } from '@/lib/errors'
import {
  POST_FILTERS,
  POST_STATUS,
  postActions,
  postReason,
  rowErrorAfterRefetch,
  visibleRowErrors,
  type PostFilter,
  type RowError,
} from './destinations'
import { relativeWhen } from './model'
import styles from './ChannelEditDialog.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

const PAGE_SIZE = 20

/**
 * "Fila e histórico" da aba Destinos: o que vai sair, o que saiu e o que
 * parou, com o motivo (inclusive os pulados pela segurança, pela pausa ou pelo
 * limite do dia). Cancelar e Tentar de novo valem na hora.
 */
export function DestinationPostsQueue({ slug, destinationEnabled }: { slug: string; destinationEnabled: boolean }) {
  const [filter, setFilter] = useState<PostFilter>('all')
  const [page, setPage] = useState(1)
  const statuses = POST_FILTERS.find((f) => f.value === filter)?.statuses ?? []
  const params = { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE, statuses }
  const posts = useDestinationPosts(slug, params)
  const action = useDestinationPostAction(slug)
  const qc = useQueryClient()
  // Cada linha tem o seu "ocupado" e o seu erro: cancelar B não pode liberar A.
  // O "ocupado" dura até a fila ser relida (a mutação espera o refetch), e o
  // erro fica preso ao estado relido: some só numa mudança posterior a ele.
  const [busy, setBusy] = useState<ReadonlySet<number>>(new Set())
  const [rowErrors, setRowErrors] = useState<Record<number, RowError>>({})

  function run(kind: DestinationPostAction, post: DestinationPost) {
    const id = post.id
    if (busy.has(id)) return
    setBusy((b) => new Set(b).add(id))
    setRowErrors(({ [id]: _drop, ...rest }) => rest)
    action
      .mutateAsync({ action: kind, postId: id })
      .catch((err: unknown) => {
        const page = qc.getQueryData<DestinationPostsPage | null>(destinationPostsKey(slug, params))
        setRowErrors((e) => ({ ...e, [id]: rowErrorAfterRefetch(errorMessage(err), id, post.status, page) }))
      })
      .finally(() => {
        setBusy((b) => {
          const next = new Set(b)
          next.delete(id)
          return next
        })
      })
  }

  const data = posts.data
  return (
    <section className={styles.section} aria-busy={posts.isFetching}>
      <div className={styles.sectionHead}>
        <h3 className={styles.sectionTitle}>Fila e histórico</h3>
        <span className={styles.sectionMeta}>Cancelar e Tentar de novo valem na hora — não dependem do Salvar.</span>
      </div>
      <p className={styles.hint}>
        Tentar de novo nunca reenvia às cegas: se o post pode ter saído, o Prism confere antes no perfil (reconcilia) e
        só publica de novo se não o encontrar. Vale até 3 h depois do horário.
      </p>
      <Segmented
        size="sm"
        aria-label="Filtrar a fila"
        options={POST_FILTERS.map((f) => ({ value: f.value, label: f.label }))}
        value={filter}
        onValueChange={(v) => {
          setFilter(v)
          setPage(1)
        }}
      />
      {posts.isPending ? (
        <span className={styles.empty}>Carregando a fila…</span>
      ) : posts.isError ? (
        <QueryErrorNotice
          compact
          error={posts.error}
          label="a fila"
          onRetry={() => {
            void posts.refetch()
          }}
        />
      ) : !data || data.data.length === 0 ? (
        <span className={styles.empty}>
          {filter === 'all'
            ? 'Nenhum post ainda. Quando o ciclo de upload subir um vídeo com o destino ligado, ele aparece aqui.'
            : 'Nenhum post neste filtro.'}
        </span>
      ) : (
        <>
          <DestinationPostList
            posts={data.data}
            destinationEnabled={destinationEnabled}
            busy={busy}
            rowErrors={visibleRowErrors(rowErrors, data.data)}
            onAction={run}
          />
          {data.meta.total > PAGE_SIZE ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={data.meta.total}
              pageSizeOptions={[PAGE_SIZE]}
              onPageChange={setPage}
              itemLabel="post"
            />
          ) : null}
        </>
      )}
    </section>
  )
}

export interface DestinationPostListProps {
  posts: readonly DestinationPost[]
  /** O destino está ligado (salvo): sem isso, Tentar de novo seria recusado. */
  destinationEnabled: boolean
  busy: ReadonlySet<number>
  rowErrors: Readonly<Record<number, string>>
  onAction: (action: DestinationPostAction, post: DestinationPost) => void
  now?: Date
}

/** As linhas da fila. Presentacional — o contêiner cuida das chamadas. */
export function DestinationPostList({
  posts,
  destinationEnabled,
  busy,
  rowErrors,
  onAction,
  now = new Date(),
}: DestinationPostListProps) {
  return (
    <div>
      <div className={styles.destHead} aria-hidden>
        <span className={styles.label}>Vídeo</span>
        <span className={styles.label}>Horário</span>
        <span className={styles.label}>Status</span>
      </div>
      {posts.map((post) => {
        const status = POST_STATUS[post.status]
        const reason = postReason(post)
        const actions = postActions(post, destinationEnabled, now)
        const pending = busy.has(post.id)
        const permalink = post.permalink.startsWith('https://') ? post.permalink : ''
        return (
          <div key={post.id} className={styles.destRow} data-status={post.status}>
            <span className={styles.destVideo}>
              <span className={styles.queueText} title={post.title || post.video_id}>
                {post.title || post.video_id}
              </span>
              {reason ? (
                <span className={cn(styles.hint, styles.destReason)} title={post.last_error || undefined}>
                  {reason}
                  {post.status === 'failed' && post.last_error_code ? ` (código ${post.last_error_code})` : ''}
                </span>
              ) : null}
              {permalink ? (
                <a className={styles.destLink} href={permalink} target="_blank" rel="noreferrer">
                  Ver no Instagram <ExternalLink aria-hidden size={12} />
                </a>
              ) : null}
            </span>
            <span className={styles.queueWhen}>
              {relativeWhen(post.publish_at, now)}
              {post.published_at ? (
                <span className={styles.destPublished}>saiu {relativeWhen(post.published_at, now)}</span>
              ) : null}
            </span>
            <span className={styles.queueStatus}>
              <Chip variant={status.variant}>{status.label}</Chip>
            </span>
            {actions.cancel || actions.retry ? (
              <div className={styles.destActions}>
                {actions.cancel ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    leadingIcon={pending ? <Spinner /> : <X />}
                    disabled={pending}
                    onClick={() => {
                      onAction('cancel', post)
                    }}
                  >
                    Cancelar
                  </Button>
                ) : null}
                {actions.retry ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    leadingIcon={pending ? <Spinner /> : <RotateCcw />}
                    disabled={pending}
                    title={
                      actions.reconcilesFirst
                        ? 'Já houve uma chamada de envio: o Prism confere no perfil antes de publicar de novo.'
                        : undefined
                    }
                    onClick={() => {
                      onAction('retry', post)
                    }}
                  >
                    {actions.reconcilesFirst ? 'Conferir e tentar de novo' : 'Tentar de novo'}
                  </Button>
                ) : null}
              </div>
            ) : null}
            {actions.hint ? <p className={cn(styles.hint, styles.destActions)}>{actions.hint}</p> : null}
            {rowErrors[post.id] ? (
              <p className={cn(styles.error, styles.destActions)} role="alert">
                {rowErrors[post.id]}
              </p>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
